/**
 * MoonPhasePanel — current moon phase + sign + illumination, on the shared
 * `.moon-card` Liquid Glass surface (verbatim classes from MoonCard / index.css).
 *
 * The API returns phase + sign as ASCII slugs (services/api/.../domain/lunar);
 * this panel maps them to the dignified RU labels used across the platform
 * (mirrors apps/webapp/src/lib/lunar/copy.ts — no emoji, plain timing language).
 */
import type { ReactElement } from "react";

import type { LunarToday, PhaseName } from "@/lib/lunar-api";

const PHASE_LABELS: Readonly<Record<PhaseName, string>> = {
  new: "Новолуние",
  "waxing-crescent": "Растущий серп",
  "first-quarter": "Первая четверть",
  "waxing-gibbous": "Растущая выпуклая",
  full: "Полнолуние",
  "waning-gibbous": "Убывающая выпуклая",
  "last-quarter": "Последняя четверть",
  balsamic: "Бальзамическая луна",
};

const PHASE_TAGLINES: Readonly<Record<PhaseName, string>> = {
  new: "Намерения, тишина, посев.",
  "waxing-crescent": "Импульс и первые шаги.",
  "first-quarter": "Преодоление сопротивления.",
  "waxing-gibbous": "Доработка и проверка деталей.",
  full: "Кульминация и проявление.",
  "waning-gibbous": "Распространение опыта.",
  "last-quarter": "Переосмысление итогов.",
  balsamic: "Отпускание и подготовка.",
};

const SIGN_LABELS: Readonly<Record<string, string>> = {
  aries: "Овен",
  taurus: "Телец",
  gemini: "Близнецы",
  cancer: "Рак",
  leo: "Лев",
  virgo: "Дева",
  libra: "Весы",
  scorpio: "Скорпион",
  sagittarius: "Стрелец",
  capricorn: "Козерог",
  aquarius: "Водолей",
  pisces: "Рыбы",
};

const SIGN_GLYPHS: Readonly<Record<string, string>> = {
  aries: "♈",
  taurus: "♉",
  gemini: "♊",
  cancer: "♋",
  leo: "♌",
  virgo: "♍",
  libra: "♎",
  scorpio: "♏",
  sagittarius: "♐",
  capricorn: "♑",
  aquarius: "♒",
  pisces: "♓",
};

/** "Дева" for a known slug, else the raw slug (never fabricate a sign). */
function signLabel(sign: string): string {
  return SIGN_LABELS[sign] ?? sign;
}

/** "14°22′ ♍ · растущая" — mono position line, mirrors MoonCard `.v`. */
function positionLine(data: LunarToday): string {
  const whole = Math.floor(data.sign_degree);
  const minutes = Math.round((data.sign_degree - whole) * 60);
  const glyph = SIGN_GLYPHS[data.sign] ?? "";
  const waxing = data.phase.is_waxing ? "растущая" : "убывающая";
  const deg = `${whole}°${minutes.toString().padStart(2, "0")}′`;
  return `${deg} ${glyph} · ${waxing}`.trim();
}

/**
 * Liquid Glass moon glyph (verbatim from MoonCard) — kept local so this panel
 * has no dependency on the template page's component graph.
 */
function MoonGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 36 36" aria-hidden="true">
      <circle cx="18" cy="18" r="14" fill="#252B45" stroke="#9AA0B0" strokeWidth="0.8" />
      <path d="M18 4a14 14 0 0 1 0 28a9 9 0 0 0 0-28z" fill="#F5F2EC" />
    </svg>
  );
}

export function MoonPhasePanel({ data }: { readonly data: LunarToday }): ReactElement {
  const label = PHASE_LABELS[data.phase.name];
  const tagline = PHASE_TAGLINES[data.phase.name];
  const illumination = Math.round(data.phase.illumination_pct);
  const voc = data.void_of_course;
  return (
    <article className="moon-card">
      <div className="phase">
        <MoonGlyph />
        <div className="phase-text">
          <div className="l">{label}</div>
          <div className="v">{positionLine(data)}</div>
        </div>
      </div>
      <h3>Луна в знаке {signLabel(data.sign)}</h3>
      <p>
        {tagline} Освещённость диска — {illumination}%.
        {voc?.is_active
          ? ` Луна без курса до перехода в знак ${signLabel(voc.next_sign)} — отложите важные решения.`
          : null}
      </p>
    </article>
  );
}
